// ─────────────────────────────────────────────
//  TimelineMdCallRail — the Daily List's MD Call, restyled for column 1.
//
//  WHY A SECOND MD CALL LIST EXISTS ON THIS PAGE (asked for 2026-08-13):
//  the right column's MD CALL LIST answers "who do I ring, and who has left" —
//  a table, call position + departures. This one answers the Daily List's
//  question instead: "who is on call ACROSS THE CAMPUSES today" — post-call
//  slots, locums, and the campus each doctor is working at, carried on the
//  number badge (red Wesley · blue Tampa · green Carrollwood).
//
//  ⚠️ THE TWO LISTS ANSWER DIFFERENT QUESTIONS, AND MUST NOT BE "ALIGNED".
//  This one is the WHOLE OPERATION — every attending working today across all
//  facilities, which is what the campus colour is for. The right-hand card is
//  scoped to THIS facility's roster. They read in opposite directions too:
//  `sortMdCall` here, ascending call order there. Where they do share a fact —
//  who has departed, leaving times, and now the departure action itself — they
//  are fed the SAME props, never a second copy of the logic.
//
//  Badge/colour/sort all come from `features/sheet-import/attendingCallList`,
//  the same four functions `MdCallPanel` calls, so the Daily List and this rail
//  cannot drift apart.
//
//  ── EDIT MODE, AND WHY IT IS SAFE HERE NOW (2026-08-17) ────────────────────
//  This card was made READ-ONLY ON PURPOSE on 2026-08-13: it lists the whole
//  operation, so most of its rows are doctors on OTHER campuses whom this board
//  has no business departing, and a ✕ on every row was both wrong and too wide
//  for a 179px column. That reasoning has not changed — what changed is that
//  there is now a primitive that answers "which campus is this device standing
//  at" and FAILS CLOSED (domain/facilityResolver via useDeviceFacility). So the
//  rail gains an Edit toggle whose controls appear on a row ONLY when all of:
//
//    1. the caller says this account may already change the board
//       (`canEditBoard` — the same canManageAssignments the ✕ Depart on the
//       right-hand card is gated on; no new permission is invented here);
//    2. the device has RESOLVED a campus — `checking`, "none" and "ambiguous"
//       are all NO, because "we could not tell" is never permission; and
//    3. the row's own campus IS the campus this board is showing (writes land on
//       the VIEWED board's roster, so acting on a Tampa row while looking at
//       Wesley Chapel would write Tampa's change into Wesley's board).
//
//  ⚠️ AN ADMIN HAS NO GEOFENCE BOUNDARIES (product decision, 2026-08-17).
//  Admins support every campus and are routinely nowhere near the one they are
//  repairing, so condition (2) would lock out the only people who can fix a
//  board remotely. `isAdmin` skips it entirely.
//
//  ⚠️ CONDITION (3) NO LONGER APPLIES TO ADMINS EITHER (owner, 2026-08-20:
//  *"the admin have no geofence. the admin can change and edit from any
//  facility"*). It had been kept as a correctness rule, and the reasoning was
//  half right: every write DOES land on the viewed board. But the effect was
//  that the rows an admin most needs — a doctor on NO campus's board today, who
//  is exactly the person you reach for when a board is short — were locked with
//  "Not on today's boards", which reads as a fault and is not one. An admin now
//  edits any row; the write still targets the board on screen, and a row whose
//  doctor is leading a DIFFERENT campus today says so next to the controls
//  rather than hiding them. For a non-admin, (3) stands unchanged.
//
//  Rows that fail (3) stay visible and say WHY in words. Greying them out
//  silently would read as a broken app on the one card whose entire purpose is
//  showing rows this facility does not own.
//
//  Everything else stays delegated: departure is `DepartureFlow` (mandatory
//  per-room reassignment + AIC handoff), naming the AIC is one `onMakeAic`
//  callback the page owns. No second departure path lives here.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../../theme/tokens";
import {
  sortMdCall,
  mdCallBadge,
  mdCallLocationRole,
  mdCallBadgeColor,
} from "../../features/sheet-import/attendingCallList";
import MdCallEditControls, {
  railCampusOf,
  campusName,
  leadTitle,
} from "./MdCallEditControls";
import { boardEditGate } from "../../domain/boardEditGate";

const { color, font, radius } = tokens;

const norm = (n) => String(n || "").replace(/^dr\.?\s+/i, "").trim().toLowerCase();

// ── ROW BUILDER (pure) ──────────────────────────────────────────────────────
//
// Joins the SHEET's call list (which carries the badge facts: code, callOrder,
// postCall, wc) to the TIMELINE's rows (which carry the clinical facts: the
// real person `id`, and the locum leaving time) — because neither source has
// both, and inventing the missing half is how a board starts lying.
//
//   callList     roster.mdCallList — [] when today's sheet was never imported
//   mdCall       mdCallRows() output: {id, name, callOrder, isLocum, leaveLabel}
//   staffDoctors the staff master, for shortCode → person id
//   aicCode      the LIVE roster AIC's shortCode (NOT the sheet's import-time
//                isAIC flag — after an AIC handoff the tag must move)
//
// ⚠️ `id` MUST be the real person id wherever one exists: `renderDepart` looks
// the row back up in the staff master by it, and a row whose id it cannot
// resolve silently renders no Depart control at all.
export function buildRailMdCall({
  callList = [],
  mdCall = [],
  staffDoctors = [],
  aicCode = "",
} = {}) {
  const sheet = Array.isArray(callList) ? callList.filter((e) => e && e.code) : [];
  const rows = Array.isArray(mdCall) ? mdCall : [];
  const staff = Array.isArray(staffDoctors) ? staffDoctors : [];
  const live = String(aicCode || "").toUpperCase();

  // No sheet today: fall back to the rows the rest of the page is already
  // using. The campus colour simply does not render — the sheet is the only
  // thing that knows a post-call slot or a cross-campus assignment, and a
  // guessed tint on a clinical board is worse than none.
  if (!sheet.length) {
    return rows.map((r) => ({
      id: r.id,
      code: "",
      name: r.name,
      badge: r.isLocum ? "L" : r.callOrder != null ? String(r.callOrder) : "—",
      isLocum: Boolean(r.isLocum),
      leaveLabel: r.leaveLabel || "",
      isAIC: Boolean(r.isAic),
      // `entry` feeds the colour helpers. With no sheet there is nothing for
      // them to match on, so they return null and the badge keeps its default.
      entry: { code: "", name: r.name, callOrder: r.callOrder, isLocum: r.isLocum },
    }));
  }

  const byCode = (code) =>
    staff.find((d) => d && String(d.shortCode || "").toUpperCase() === code) || null;

  // ⚠️ THIS LIST IS THE WHOLE OPERATION, NOT THIS CAMPUS. It is the Daily
  // List's MD Call verbatim: every attending working today across ALL
  // facilities, which is exactly what the campus-coloured badge is for. The
  // RIGHT-hand card is the facility-scoped one ("who can I ring HERE"), and the
  // two are deliberately different lists — do not "fix" one to match the other.
  return sortMdCall(sheet).map((e) => {
    const code = String(e.code || "").toUpperCase();
    const person = byCode(code);
    const name = person?.name || e.name || e.code;
    // The roster row, when this doctor is on THIS board today. It carries the
    // leaving time and the id `personForMdRow` resolves.
    const row =
      (person && rows.find((r) => r.id === person.id || r.id === person.uid)) ||
      rows.find((r) => norm(r.name) === norm(name)) ||
      null;
    return {
      // The roster row's id when this doctor is on THIS board — it is what the
      // departed map is keyed by, so a doctor who has gone home here still
      // strikes through. A doctor working at another campus holds no row on
      // this board and gets the synthetic `mdcall:CODE`, which matches nothing
      // in that map: this page knows nothing about their day and says nothing
      // about it. Same id the rest of the page uses for a sheet-only entry.
      id: row?.id || `mdcall:${code}`,
      code,
      name,
      badge: mdCallBadge(e),
      isLocum: Boolean(e.isLocum || row?.isLocum || e.callOrder == null),
      leaveLabel: row?.leaveLabel || "",
      isAIC: live ? code === live : Boolean(e.isAIC),
      entry: { ...e, name },
    };
  });
}

// ── COMPONENT ───────────────────────────────────────────────────────────────
export default function TimelineMdCallRail({
  rows = [],
  facilityBoards = {},
  facilities = [],
  departed = {},
  // Everything below is opt-in. Omitted, this renders exactly the read-only
  // card it always did — no Edit button, no gate line, no controls.
  canEditBoard = false,
  // The whole return of `useDeviceFacility`: { facilityId, verification,
  // reason, checking, retry }.
  deviceFacility = null,
  // The campus THIS board is showing. Writes go to it, so it must equal the
  // resolved device campus before anything is editable.
  viewedFacilityId = null,
  // ⚠️ AN ADMIN HAS NO GEOFENCE BOUNDARIES (product decision, 2026-08-17).
  // Admins support every campus and are routinely nowhere near the one they are
  // fixing, so a location gate would lock out the only people who can repair a
  // board remotely. It skips the DEVICE conditions entirely — never the
  // correctness one below.
  isAdmin = false,
  onMakeAic = null,
  renderDepart = null,
}) {
  const aicByFacility = facilityBoards.aicByFacility || {};
  const doctorsByFacility = facilityBoards.doctorsByFacility || {};
  const roleOf = (entry) => mdCallLocationRole(entry, aicByFacility, facilities);
  const tintOf = (entry) => mdCallBadgeColor(entry, { ...facilityBoards, facilities });

  const [editing, setEditing] = useState(false);

  // ⚠️ THE GATE LIVES IN domain/boardEditGate NOW, not here. Its rules are a
  // product decision that has already been revised twice (2026-08-17 geofence,
  // 2026-08-20 admin bypass) and the Doctors Needed box's release menu writes to
  // the same roster — a second copy would be a second thing to revise. The
  // reasoning behind each rule moved with it; this card remains its reference
  // caller, and its tests are what prove the extraction stayed faithful.
  //
  // `campusName` is INJECTED because it lives in a component: the domain layer
  // must not import the view. Bound to this card's `facilities` here.
  const { gateOpen, editableCampusId, gateNote, canRetry } = boardEditGate({
    canEditBoard,
    deviceFacility,
    viewedFacilityId,
    isAdmin,
    campusName: (id) => campusName(id, facilities),
  });

  const title = gateOpen ? leadTitle(editableCampusId, facilities) : "AIC";

  return (
    <section style={s.card} data-testid="rail-md-call">
      <div style={s.head}>
        <div style={s.label}>MD CALL</div>
        {gateOpen && (
          <button
            type="button"
            style={editing ? s.editBtnOn : s.editBtn}
            aria-pressed={editing}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? "Done" : "Edit"}
          </button>
        )}
      </div>

      {gateNote && (
        <p style={s.gateNote}>
          {gateNote}
          {canRetry && (
            <button type="button" style={s.retryBtn} onClick={deviceFacility.retry}>
              Retry
            </button>
          )}
        </p>
      )}

      {rows.length === 0 ? (
        <p style={s.empty}>No MD call list on today's board.</p>
      ) : (
        <ul style={s.list} aria-label="MD call list">
          {rows.map((r) => {
            const role = roleOf(r.entry);
            // A departed doctor KEEPS their row, struck through — the same rule
            // the right-hand card and the Daily List follow. Dropping the row
            // would leave a reader unable to tell "gone home" from "never on
            // call today", and those want opposite actions.
            const isDeparted = Boolean(departed[r.id]);
            const tint = tintOf(r.entry);
            const rowCampus = editing && gateOpen
              ? railCampusOf(r.entry, { aicByFacility, doctorsByFacility, facilities })
              : null;
            // An admin edits any row. A non-admin still edits only rows whose
            // campus is the board on screen — for them the device campus and the
            // viewed campus are already equal, so this is the same rule it was.
            const editable = editing && gateOpen &&
              (isAdmin || (!!rowCampus && rowCampus === editableCampusId));
            // Said, not hidden: an admin acting on a doctor who is leading
            // another campus today should see that before they act, because the
            // write lands HERE and not there.
            const otherCampus =
              editable && rowCampus && rowCampus !== editableCampusId
                ? campusName(rowCampus, facilities)
                : null;
            return (
              <li key={r.id} style={s.row}>
                <span
                  style={{
                    ...s.badge,
                    background: tint || (r.entry?.wc ? color.accent.error : color.accent.primary),
                    ...(isDeparted ? s.badgeDeparted : null),
                  }}
                  title={role?.label || undefined}
                >
                  {r.badge}
                </span>
                <span style={s.body}>
                  <span style={s.nameLine}>
                    <span style={isDeparted ? s.nameDeparted : s.name} title={r.name}>
                      {r.name}
                    </span>
                  </span>
                  {/* Second line, and only when there is something to say. In a
                      179px column a tag beside the name pushes every name to a
                      second line anyway; below it, the name stays whole. */}
                  {(role || r.isAIC || r.leaveLabel || isDeparted) && (
                    <span style={s.meta}>
                      {isDeparted && <span style={s.departedTag}>DEPARTED</span>}
                      {role && (
                        <span style={{ ...s.roleTag, color: role.color, borderColor: role.color }}>
                          {role.label}
                        </span>
                      )}
                      {!role && r.isAIC && <span style={s.aicTag}>AIC</span>}
                      {r.leaveLabel && <span style={s.leaveTag}>leaves {r.leaveLabel}</span>}
                    </span>
                  )}
                  {/* Controls (or the named lock) get their OWN line, under the
                      name — see MdCallEditControls for why. Keyed by campus so
                      a row that changes campus resets its own busy/error.

                      A DEPARTED row gets none of it, and needs no lock chip to
                      explain that: the DEPARTED tag one line up is the reason,
                      already in words. Same rule the right-hand card follows
                      (TimelineSidePanels:256 suppresses its ✕ the same way) —
                      somebody who has gone home cannot be made AIC and cannot
                      depart twice. */}
                  {editing && gateOpen && !isDeparted && (
                    <MdCallEditControls
                      key={`${r.id}:${rowCampus || "none"}`}
                      row={r}
                      editable={editable}
                      otherCampus={otherCampus}
                      rowCampus={rowCampus}
                      title={title}
                      facilities={facilities}
                      onMakeAic={onMakeAic}
                      renderDepart={renderDepart}
                    />
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// Rail language, not Daily List language: the same card shell, label size and
// letter-spacing TimelineRail's ROOMS RUNNING block uses, so this reads as the
// bottom of that rail rather than a panel that wandered in from another page.
const s = {
  card: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    padding: "9px 10px 10px",
    minWidth: 0,
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    marginBottom: 7,
  },
  label: {
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: ".7px",
    color: color.text.muted,
  },
  // 44px both ways — this is pressed one-handed in a corridor, and the header
  // is where a thumb lands first. Outlined rather than filled: turning on an
  // edit mode is not an emergency, and colour weight here would compete with
  // the strips that are.
  editBtn: {
    minWidth: 44,
    minHeight: 44,
    padding: "0 10px",
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    fontFamily: "inherit",
    color: color.accent.primary,
    background: "transparent",
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    flexShrink: 0,
  },
  editBtnOn: {
    minWidth: 44,
    minHeight: 44,
    padding: "0 10px",
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    fontFamily: "inherit",
    color: color.surface.card,
    background: color.accent.primary,
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    flexShrink: 0,
  },
  gateNote: {
    margin: "0 0 7px",
    fontSize: 9.5,
    lineHeight: 1.35,
    color: color.text.muted,
  },
  retryBtn: {
    marginLeft: 6,
    minHeight: 44,
    minWidth: 44,
    padding: "0 8px",
    fontSize: 9.5,
    fontWeight: font.weight.bold,
    fontFamily: "inherit",
    color: color.accent.primary,
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  list: { listStyle: "none", margin: 0, padding: 0 },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: 7,
    padding: "5px 0",
    borderTop: `1px solid ${color.border.default}`,
    minWidth: 0,
  },
  // Smaller than the Daily List's 26px pill — this column is 179px — but still
  // a filled circle carrying the campus colour, which is the whole point of it.
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 21,
    height: 21,
    padding: "0 5px",
    borderRadius: radius.full,
    color: "#ffffff",
    fontSize: 9.5,
    fontWeight: 800,
    flexShrink: 0,
    marginTop: 1,
  },
  // Departure drains the badge of its campus colour: the tint answers "where is
  // this doctor working today", and the answer for someone who has gone home is
  // nowhere. Struck-through name + grey badge agree; a red badge over a struck
  // name would read as an alarm.
  badgeDeparted: { background: color.text.muted },
  body: { minWidth: 0, flex: 1 },
  nameLine: { display: "flex", alignItems: "center", gap: 4, minWidth: 0 },
  name: {
    fontSize: 11.5,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    lineHeight: 1.25,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  nameDeparted: {
    fontSize: 11.5,
    color: color.text.muted,
    textDecoration: "line-through",
    lineHeight: 1.25,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  meta: { display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginTop: 3 },
  roleTag: {
    fontSize: 8.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".3px",
    border: "1px solid",
    background: "transparent",
    borderRadius: radius.sm,
    padding: "0 4px",
  },
  aicTag: {
    fontSize: 8.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".3px",
    color: color.accent.primary,
    border: `1px solid ${color.accent.primary}`,
    background: "transparent",
    borderRadius: radius.sm,
    padding: "0 4px",
  },
  departedTag: {
    fontSize: 8.5,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    color: color.text.muted,
  },
  leaveTag: {
    fontSize: 8.5,
    color: color.text.muted,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.full,
    padding: "0 5px",
    whiteSpace: "nowrap",
  },
  empty: { fontSize: 10, color: color.text.muted, fontStyle: "italic", margin: 0 },
};
